import * as React from "react"
import { Combobox as ComboboxPrimitive } from "@base-ui/react/combobox"
import { Check, ChevronDown } from "lucide-react"

import { cn } from "@/lib/utils"
import { Input } from "./form"

/**
 * A list that forbids, and that you can still type into.
 *
 * The sibling of ./autocomplete, for the opposite kind of field. That one holds
 * the customer's own vocabulary, so a value nobody has seen before is a value.
 * This one holds a choice among records that exist — a 5S checklist, say —
 * where a value that matches no row is an answer the server will refuse. Base
 * UI's Combobox is that: the rows narrow as you type, but only a row can be
 * chosen, and what is submitted is the row's value rather than the text in the
 * box.
 *
 * ## Why not a native `<select>`
 *
 * The same reason ./autocomplete gives for `<datalist>`: the popup is browser
 * UI. An `<option>` holds one line of plain text, so a row cannot carry a name
 * *and* a note about it — and on the work-centre form it was precisely the
 * missing second line that hid the right answer. The checklist a customer had
 * named "Obrada stranica 2" was listed under its type code, `LIGNUM 2`, and two
 * administrators looked straight past it.
 *
 * ## Shape
 *
 * Deliberately the same parts as ./autocomplete, so a call site reads the same
 * whichever of the two it uses. `ComboboxContent` is the whole Portal →
 * Positioner → Popup stack.
 */

function Combobox<ItemValue>(
  props: Omit<ComboboxPrimitive.Root.Props<ItemValue>, "items"> & {
    items?: readonly ItemValue[] | undefined
  },
) {
  return <ComboboxPrimitive.Root {...props} />
}

function ComboboxInputGroup({
  className,
  ...props
}: ComboboxPrimitive.InputGroup.Props) {
  return (
    <ComboboxPrimitive.InputGroup
      data-slot="combobox-input-group"
      className={cn("relative", className)}
      {...props}
    />
  )
}

/*
 * ./form's `Input` through `render`, with Base UI's id dropped on the way —
 * see `AutocompleteInput` for why both halves of that matter.
 */
function ComboboxInput({ className, ...props }: ComboboxPrimitive.Input.Props) {
  return (
    <ComboboxPrimitive.Input
      data-slot="combobox-input"
      className={cn("pr-12", className)}
      {...props}
      render={({ id: _baseUiId, ...inputProps }) => <Input {...inputProps} />}
    />
  )
}

/** The caret. 44px, and out of the tab order — see `AutocompleteTrigger`. */
function ComboboxTrigger({
  className,
  children,
  ...props
}: ComboboxPrimitive.Trigger.Props) {
  return (
    <ComboboxPrimitive.Trigger
      data-slot="combobox-trigger"
      className={cn(
        "group/combobox-trigger text-muted-foreground hover:text-foreground absolute top-1/2 right-1 -translate-y-1/2",
        "flex size-11 items-center justify-center rounded-md transition-colors",
        "focus-visible:ring-ring/50 focus-visible:ring-3 focus-visible:outline-none",
        "disabled:pointer-events-none disabled:opacity-50",
        className,
      )}
      {...props}
    >
      {children ?? (
        <ChevronDown
          className="size-4.5 transition-transform duration-150 group-data-[popup-open]/combobox-trigger:rotate-180"
          aria-hidden
        />
      )}
    </ComboboxPrimitive.Trigger>
  )
}

/* As wide as its rows, never narrower than the field. See `AutocompleteContent`. */
function ComboboxContent({
  align = "start",
  alignOffset = 0,
  side = "bottom",
  sideOffset = 4,
  className,
  ...props
}: ComboboxPrimitive.Popup.Props &
  Pick<
    ComboboxPrimitive.Positioner.Props,
    "align" | "alignOffset" | "side" | "sideOffset"
  >) {
  return (
    <ComboboxPrimitive.Portal>
      <ComboboxPrimitive.Positioner
        className="isolate z-50 outline-none"
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
      >
        <ComboboxPrimitive.Popup
          data-slot="combobox-content"
          className={cn("z-50 max-h-(--available-height) max-w-(--available-width) w-max min-w-[max(12rem,var(--anchor-width))] origin-(--transform-origin) overflow-x-hidden overflow-y-auto overscroll-contain rounded-lg bg-popover p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10 duration-100 outline-none data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:overflow-hidden data-closed:fade-out-0 data-closed:zoom-out-95", className)}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  )
}

function ComboboxList({ className, ...props }: ComboboxPrimitive.List.Props) {
  return (
    <ComboboxPrimitive.List
      data-slot="combobox-list"
      className={cn("flex flex-col", className)}
      {...props}
    />
  )
}

/**
 * One row, with a tick on the one already chosen.
 *
 * The tick is the one thing this has that `AutocompleteItem` does not. An
 * autocomplete's value is the text in the box, so the box already says what
 * is chosen; here the box is also where you type to filter, so once somebody
 * has typed "obrada" the chosen row needs its own mark. `pr-8` keeps a long
 * note clear of it.
 */
function ComboboxItem({
  className,
  children,
  ...props
}: ComboboxPrimitive.Item.Props) {
  return (
    <ComboboxPrimitive.Item
      data-slot="combobox-item"
      className={cn(
        "relative flex min-h-11 cursor-default flex-col justify-center gap-0.5 rounded-md py-1.5 pr-8 pl-2.5 text-sm outline-hidden select-none data-highlighted:bg-accent data-highlighted:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50",
        className,
      )}
      {...props}
    >
      {children}
      <ComboboxPrimitive.ItemIndicator className="absolute top-1/2 right-2.5 -translate-y-1/2">
        <Check className="size-4" aria-hidden />
      </ComboboxPrimitive.ItemIndicator>
    </ComboboxPrimitive.Item>
  )
}

/** The second line of a row. See `AutocompleteItemNote`. */
function ComboboxItemNote({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="combobox-item-note"
      className={cn("text-muted-foreground text-xs leading-snug", className)}
      {...props}
    />
  )
}

/** Mounted always, collapsed when silent. See `AutocompleteEmpty`. */
function ComboboxEmpty({ className, ...props }: ComboboxPrimitive.Empty.Props) {
  return (
    <ComboboxPrimitive.Empty
      data-slot="combobox-empty"
      className={cn(
        "text-muted-foreground px-2.5 py-3 text-sm leading-snug empty:p-0",
        className,
      )}
      {...props}
    />
  )
}

export {
  Combobox,
  ComboboxInputGroup,
  ComboboxInput,
  ComboboxTrigger,
  ComboboxContent,
  ComboboxList,
  ComboboxItem,
  ComboboxItemNote,
  ComboboxEmpty,
}
